import React from 'react';

import { ColumnDefinition } from 'components/Grid/types';
import { OptionsOperator } from 'components/Grid/operators/Options';
import DueDate from 'components/DueDate';
import ArchivedPill from 'components/ArchivedPill';

import { EntityModules, ProtectorType } from 'core/constants';

import { formatLibraryId } from 'utils/formatters/libraryId';

import { CONTROL_FIELD, CONTROL_STATUS_LABEL, ControlStatus, ControlStatusFilter } from '../../constants';
import { EmptyCell } from '../../styles';
import { ControlRow } from '../../types';
import ControlStatusChip from '../ControlStatusChip';

type ChoiceField = { value?: { key: string; value: string }[] | null };

const statusOf = (row: ControlRow): ControlStatus | undefined =>
  (row[CONTROL_FIELD.status] as ChoiceField | undefined)?.value?.[0]?.key as ControlStatus | undefined;

const STATUS_OPTIONS = Object.values(ControlStatusFilter).map((value) => ({
  value,
  label: CONTROL_STATUS_LABEL[value],
}));

export const basicControlsColumns = [
  {
    field: CONTROL_FIELD.id,
    headerName: 'ID',
    protectorType: ProtectorType.Number,
    renderCell: ({ value }: { value: { value: number | null } }) =>
      formatLibraryId(EntityModules.Controls, value.value),
  },
  {
    field: CONTROL_FIELD.name,
    headerName: 'Control',
    protectorType: ProtectorType.ShortText,
  },
  {
    field: CONTROL_FIELD.description,
    headerName: 'Description',
    protectorType: ProtectorType.LongText,
  },
  {
    field: CONTROL_FIELD.category,
    headerName: 'Category',
    protectorType: ProtectorType.SingleChoice,
    pickerFilter: true,
  },
  {
    field: CONTROL_FIELD.owner,
    headerName: 'Owner',
    protectorType: ProtectorType.User,
  },
  {
    field: 'libraryStatus',
    replacesField: CONTROL_FIELD.status,
    headerName: 'Status',
    width: 150,
    sortingField: CONTROL_FIELD.status,
    protectorType: ProtectorType.SingleChoice,
    nativeFilter: 'status',
    filterOperators: OptionsOperator(STATUS_OPTIONS, { allowIsNot: true }),
    renderCell: ({ row }: { row: ControlRow }) => {
      if (row.archivedAt) {
        return <ArchivedPill />;
      }
      const status = statusOf(row);
      return status ? <ControlStatusChip status={status} /> : <EmptyCell>-</EmptyCell>;
    },
  },
  {
    field: CONTROL_FIELD.dueDate,
    headerName: 'Due date',
    protectorType: ProtectorType.DateTime,
    renderCell: ({ value }: { value: { value: number | null } }) =>
      value.value ? <DueDate date={value.value} showTime /> : <EmptyCell>-</EmptyCell>,
  },
] as unknown as ColumnDefinition[];
